import { useState, useEffect, useMemo, useCallback } from "react";
import { UsersThreeIcon, ListBulletsIcon, KanbanIcon, PlusIcon } from "@phosphor-icons/react";
import { CandidateList } from "components/candidates/CandidateList";
import { CandidacyKanbanView } from "./components/CandidacyKanbanView";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Switch } from "components/ui/switch";
import { Button } from "components/ui/button";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { FilterBar } from "shared/components/filterBar";
import type { FilterSection } from "shared/components/filterBar/types";
import { InviteCandidateModal } from "screens/buildings/components/rentalTab/modals/inviteCandidateModal";
import { candidacyPhaseColumns } from "./candidacyPhaseConfig";
import { NieuweKandidatenBanner } from "components/candidates/NieuweKandidatenBanner";

export interface Application {
  id: string;
  status: string;
  // True when this row comes from the Invited table (vs a real Application). Set
  // by the backend; source of truth for archive/email routing. Status alone is
  // ambiguous ("reminder-sent"/"forwarded-to-broker" exist in both tables).
  isInvited?: boolean;
  personalDetails: {
    firstName: string;
    lastName: string;
    email: string;
    id: string;
  };
  createDate: Date;
  visitDate: Date;
  numberOfAdults: number;
  numberOfChildren: number;
  hasPets: boolean;
  smoking: boolean;
  checklist: {
    label: string;
    isChecked: boolean;
    explanation: string | null;
  }[];
  unitId: string;
  notInterested?: {
    reason: string;
  } | null;
  residents: any[];
  moveDate: {
    agreement: string;
    explanation: Date | null;
  };
  buildingId: string;
}

interface BuildingOption {
  key: string;
  label: string;
}

const Candidacies = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep-linked unit filter from leegstand-style tables: /rental/candidacies?unitId=X
  const unitIdFromUrl = searchParams.get("unitId") || undefined;
  const { session } = useSession();
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [candidacies, setCandidacies] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInviteCandidateModalOpen, setIsInviteCandidateModalOpen] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0); // Used to trigger list refresh
  // FilterBar states
  const [filterBarSearchText, setFilterBarSearchText] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [buildingFilters, setBuildingFilters] = useState<string[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<BuildingOption[]>([]);
  const [archivedFilters, setArchivedFilters] = useState<string[]>([]);
  // Kandidaten van verhuurde units worden standaard getoond — ze moeten nog
  // manueel afgehandeld worden. Deze filter verbergt ze op aanvraag.
  const [rentedUnitFilters, setRentedUnitFilters] = useState<string[]>([]);
  const excludeRentedUnits = rentedUnitFilters.includes('hideRented');

  // Status options for filtering - using centralized phase config
  const statusOptions = useMemo(() =>
    candidacyPhaseColumns.map(column => ({
      key: column.id,
      label: column.label
    }))
  , []);

  // Fetch buildings for filter options using the simple endpoint
  useEffect(() => {
    if (!session) return;

    const fetchBuildings = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/simple`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const data = await response.json();
          const buildings = data.buildings || [];
          const options: BuildingOption[] = buildings.map((b: any) => ({
            key: b.id,
            label: b.id,
          }));
          setBuildingOptions(options);
        } else {
          console.error("Fout bij het ophalen van gebouwen:", response.status);
        }
      } catch (error) {
        console.error("Fout bij het ophalen van gebouwen:", error);
      }
    };

    fetchBuildings();
  }, [session]);

  // FilterBar update function
  const updateFilters = useCallback((key: string, values: string[]) => {
    switch (key) {
      case 'status':
        setStatusFilters(values);
        break;
      case 'building':
        setBuildingFilters(values);
        break;
      case 'archived':
        setArchivedFilters(values);
        break;
      case 'rentedUnits':
        setRentedUnitFilters(values);
        break;
      case 'viewMode':
        // Handle view mode changes from filter panel
        if (values.length > 0) {
          setViewMode(values[0] as 'list' | 'kanban');
        }
        break;
    }
  }, []);

  // FilterBar sections configuration
  const filterSections: FilterSection[] = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      options: statusOptions,
      selectedValues: statusFilters,
      onUpdate: (values) => updateFilters('status', values),
      color: 'moss' as const,
    },
    {
      key: 'building',
      label: 'Gebouw',
      options: buildingOptions,
      selectedValues: buildingFilters,
      onUpdate: (values) => updateFilters('building', values),
      color: 'neutral' as const,
    },
    {
      key: 'archived',
      label: 'Archief',
      options: [
        { key: 'archived', label: 'Gearchiveerde kandidaturen' },
      ],
      selectedValues: archivedFilters,
      onUpdate: (values) => updateFilters('archived', values),
      color: 'neutral' as const,
      singleSelect: true,
    },
    {
      key: 'rentedUnits',
      label: 'Verhuurde units',
      options: [
        { key: 'hideRented', label: 'Verberg kandidaten van verhuurde units' },
      ],
      selectedValues: rentedUnitFilters,
      onUpdate: (values) => updateFilters('rentedUnits', values),
      color: 'neutral' as const,
      singleSelect: true,
    },
    {
      key: 'viewMode',
      label: 'Weergave',
      options: [
        { key: 'list', label: 'Lijst' },
        { key: 'kanban', label: 'Kanban' },
      ],
      selectedValues: [viewMode],
      onUpdate: (values) => updateFilters('viewMode', values),
      color: 'neutral' as const,
      singleSelect: true,
    },
  ], [statusOptions, statusFilters, buildingOptions, buildingFilters, archivedFilters, rentedUnitFilters, viewMode, updateFilters]);

  // FilterBar filter chips
  const filterBarChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      category?: string;
      color?: 'moss' | 'brick' | 'yellow' | 'neutral';
      onRemove: () => void;
    }> = [];
    
    // Status filter chips
    statusFilters.forEach(statusKey => {
      const statusOption = statusOptions.find(s => s.key === statusKey);
      if (statusOption) {
        chips.push({
          key: `status-${statusKey}`,
          label: statusOption.label,
          category: 'Status',
          color: 'moss' as const,
          onRemove: () => setStatusFilters(prev => prev.filter(s => s !== statusKey))
        });
      }
    });

    // Building filter chips
    buildingFilters.forEach(buildingKey => {
      const buildingOption = buildingOptions.find(b => b.key === buildingKey);
      if (buildingOption) {
        chips.push({
          key: `building-${buildingKey}`,
          label: buildingOption.label,
          category: 'Gebouw',
          color: 'neutral' as const,
          onRemove: () => setBuildingFilters(prev => prev.filter(b => b !== buildingKey))
        });
      }
    });

    // Archived filter chip
    if (archivedFilters.includes('archived')) {
      chips.push({
        key: 'archived',
        label: 'Gearchiveerd',
        category: 'Archief',
        color: 'neutral' as const,
        onRemove: () => setArchivedFilters([])
      });
    }

    // Rented units filter chip
    if (excludeRentedUnits) {
      chips.push({
        key: 'rentedUnits',
        label: 'Verhuurde units verborgen',
        category: 'Verhuurde units',
        color: 'neutral' as const,
        onRemove: () => setRentedUnitFilters([])
      });
    }

    return chips;
  }, [statusFilters, statusOptions, buildingFilters, buildingOptions, archivedFilters, excludeRentedUnits]);

  // Fetch candidacies for kanban view
  const fetchCandidacies = async () => {
    if (viewMode !== 'kanban') return;
    
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/get-appointments`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setCandidacies(data);
      }
    } catch (error) {
      console.error("Error fetching candidacies:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when switching to kanban view
  useEffect(() => {
    if (viewMode === 'kanban') {
      fetchCandidacies();
    }
  }, [viewMode]);

  // Apply FilterBar filters to candidacies
  const filteredCandidacies = useMemo(() => {
    if (!candidacies.length) return [];
    
    return candidacies.filter((candidacy) => {
      // Status filter
      if (statusFilters.length > 0 && !statusFilters.includes(candidacy.status)) {
        return false;
      }

      // Search filter
      const term = filterBarSearchText.trim().toLowerCase();
      if (term) {
        const fullName = `${candidacy.personalDetails.firstName} ${candidacy.personalDetails.lastName}`.toLowerCase();
        const email = candidacy.personalDetails.email?.toLowerCase() || '';
        const unitId = candidacy.unitId?.toLowerCase() || '';
        
        const matches = (
          fullName.includes(term) ||
          email.includes(term) ||
          unitId.includes(term)
        );
        
        if (!matches) return false;
      }

      return true;
    });
  }, [candidacies, statusFilters, filterBarSearchText]);

  const handleCandidacyClick = (candidacy: Application) => {
    // Build URL with current filter parameters
    const params = new URLSearchParams();
    if (statusFilters.length > 0) {
      params.set('statusArray', statusFilters.join(','));
    }
    if (filterBarSearchText.trim()) {
      params.set('searchText', filterBarSearchText);
    }
    
    // Pass candidacy data through navigation state to avoid re-fetching
    navigate(`/rental/candidacies/${candidacy.id}?${params.toString()}`, {
      state: { candidacy }
    });
  };

  const handleOpenAddContractModal = (_contacts?: Array<{ id: number; name: string; isOnContract: boolean; resourceEntityId?: number; wasExisting?: boolean; residentData?: { email?: string | null; phoneNumber?: string | null; spokenLanguages?: string[]; nationality?: string | null; dateOfBirth?: string | null; placeOfBirth?: string | null; relationToApplicant?: string | null } }>, _unitId?: string, _contractType?: string, _totalResidents?: number) => {
    // Navigation from candidacies page to building rental tab is not supported here
    // Approve from the building's rental tab directly to open the contract modal
  };

  return (
    <>
      <div className="bleed-full-width py-2 min-h-screen !px-2">
        <header className="mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UsersThreeIcon weight="duotone" size={24} />
              <h1 className="text-xl font-bold text-gray-900">Alle Kandidaten</h1>
            </div>

            {/* View Mode Toggle and Add Button */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-background px-3 py-1.5 rounded-md shadow-sm">
                <ListBulletsIcon
                  weight='regular'
                  size={18}
                  className={viewMode === 'list' ? 'text-moss-primary' : 'text-gray-400'}
                />
                <Switch
                  id="view-mode"
                  checked={viewMode === 'kanban'}
                  onCheckedChange={(checked) => setViewMode(checked ? 'kanban' : 'list')}
                />
                <KanbanIcon
                  weight='regular'
                  size={18}
                  className={viewMode === 'kanban' ? 'text-moss-primary' : 'text-gray-400'}
                />
              </div>
              <Button
                onClick={() => setIsInviteCandidateModalOpen(true)}
                size="sm"
              >
                <PlusIcon className="h-4 w-4" weight="bold" />
                Kandidaat uitnodigen
              </Button>
            </div>
          </div>
        </header>

        {/* Nieuwe geïnteresseerde kandidaten banner */}
        <NieuweKandidatenBanner
          onCandidateClick={(id) => navigate(`/rental/candidacies/${id}`)}
        />

        {/* Render based on view mode */}
        {viewMode === 'list' ? (
          <CandidateList
            key={listRefreshKey}
            handleOpenAddContractModal={handleOpenAddContractModal}
            externalStatusFilters={statusFilters}
            externalSearchText={filterBarSearchText}
            externalBuildingFilter={buildingFilters[0]}
            externalUnitFilter={unitIdFromUrl}
            externalIncludeArchived={archivedFilters.includes('archived')}
            externalExcludeRentedUnits={excludeRentedUnits}
            hideInternalFilters={true}
            onCandidateClick={handleCandidacyClick}
          />
        ) : (
          <div>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-moss-primary mx-auto mb-4"></div>
                  <p className="text-gray-600">Kandidaten aan het laden...</p>
                </div>
              </div>
            ) : (
              <CandidacyKanbanView
                candidacies={filteredCandidacies}
                onUpdate={fetchCandidacies}
                onCandidacyClick={handleCandidacyClick}
              />
          )}
        </div>
        )}
      </div>

      {/* FilterBar at bottom */}
      <FilterBar
        searchValue={filterBarSearchText}
        onSearchChange={setFilterBarSearchText}
        searchPlaceholder="Zoek kandidaten op naam, email, unit..."
        filterChips={filterBarChips}
        filterSections={filterSections}
        showWhenEmpty={true}
        showSpacer={false}
      />

      {/* Invite Candidate Modal */}
      <InviteCandidateModal
        isOpen={isInviteCandidateModalOpen}
        onClose={() => {
          setIsInviteCandidateModalOpen(false);
          // Refresh data based on current view mode
          if (viewMode === 'kanban') {
            fetchCandidacies();
          } else {
            // Trigger list refresh by changing the key
            setListRefreshKey(prev => prev + 1);
          }
        }}
      />
    </>
  );
};

export default Candidacies;

